import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { ApiRequest } from '@/utils/axios';
import { chatStore } from '@/stores/chat.store';
import useUserStore from '@/stores/user.store';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import EmojiPick from '@/components/EmojiPick/EmojiPick';
import Login from '@/components/Login/Login';
import { SUPPORT_ID } from '@/utils/constants';
import { IMessage } from '@/utils/types/Message';
import { replaceEmojiCodesInText } from '@/utils/emojis';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import AdminAvatarMenu from '@/components/Avatars/AdminAvatarMenu';
import LevelBadge from '@/components/Levels/LevelBadge';
import { getLevelColor } from '@/utils/colors';

const Chat = () => {
    const { chat, toggleChat, sendFrom, setSendFrom } = chatStore((state) => state);
    const { user, isAuthed } = useUserStore((state) => state);

    const [messages, setMessages] = useState<IMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [picker, setPicker] = useState(false);
    const [online, setOnline] = useState<number | undefined>();

    const listRef = useRef<HTMLDivElement>(null);
    const admin = user.status !== 'USER';

    const scrollToBottom = () => {
        const el = listRef.current;
        if (!el) return;
        // Wait one frame so a freshly-rendered message participates in scrollHeight
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
        });
    };

    async function getChatHistory() {
        const res = await ApiRequest.get('/chat/history');
        setMessages(res.data as IMessage[]);
    }

    useEffect(() => {
        getChatHistory().then();
    }, []);

    useSubscription('chatMessage', () => {
        getChatHistory().then();
    }).then();

    useSubscription('chatOnline', (data) => {
        setOnline(data);
    }).then();

    useEffect(() => {
        scrollToBottom();
    }, [messages, chat]);

    // While the chat is open as a full-screen overlay, lock page scroll so
    // reaching the top/bottom of the message list doesn't scroll the page behind.
    useEffect(() => {
        if (!chat) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [chat]);

    async function send() {
        if (draft.trim().length < 3) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Alert" text="Message to small" />
            );
            return;
        }
        if (admin && sendFrom !== 0) {
            await ApiRequest.post('/admin/chat/create', { message: draft, userId: sendFrom });
        } else {
            await ApiRequest.post('/chat/create', { message: draft });
        }
        setDraft('');
        // Some backends echo own messages over websocket with a small delay — force-scroll
        // immediately so the user always sees their just-sent message land at the bottom.
        scrollToBottom();
        window.setTimeout(scrollToBottom, 300);
    }

    async function banUser(id: number, name: string) {
        await ApiRequest.post('/admin/chat/ban/byUserId', { userId: id });
        getChatHistory().then();
        notify(
            'success',
            <SuccessIcon />,
            <NotifyWrapper title="Success" text={`User: ${name} was banned`} />,
            2000
        );
    }

    async function deleteMessage(id: number) {
        await ApiRequest.post('/admin/chat/remove/byChatId', { chatIds: [id] });
        getChatHistory().then();
        notify(
            'success',
            <SuccessIcon />,
            <NotifyWrapper title="Success" text="Message was deleted" />,
            2000
        );
    }

    function shadowAdmin() {
        setSendFrom(SUPPORT_ID);
        notify(
            'success',
            <SuccessIcon />,
            <NotifyWrapper title="Success" text="Now you can chat as admin" />,
            2000
        );
    }

    return (
        <aside
            className={`surface flex flex-col overflow-hidden ${
                chat
                    ? 'fixed inset-0 z-40 w-screen h-dvh'
                    : 'max-xl:hidden xl:sticky xl:top-[80px] xl:h-[calc(100vh-92px)]'
            }`}
            style={
                chat
                    ? {
                          background: 'rgba(20,22,28,0.88)',
                          backdropFilter: 'blur(20px) saturate(140%)',
                          WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                      }
                    : undefined
            }
        >
            {/* mobile close */}
            <div className="xl:hidden flex justify-end p-2">
                <Image
                    alt=""
                    src={'/icons/closeWhite.svg'}
                    width={16}
                    height={16}
                    onClick={() => toggleChat(false)}
                    className="cursor-pointer"
                />
            </div>

            <div
                className="flex items-center justify-between px-[18px] py-4 border-b border-stroke"
                style={{ background: 'linear-gradient(180deg, rgba(139,109,255,0.10), transparent)' }}
            >
                <div className="flex items-center gap-2 font-bold text-[15px]">💬 Chat</div>
                <div className="flex items-center gap-2 text-[12.5px] font-semibold text-accent_4">
                    <span
                        className="w-2 h-2 rounded-full bg-accent_4 animate-pulse"
                        style={{ boxShadow: '0 0 10px #46e08a' }}
                    />
                    <span>{online ?? 0} online</span>
                    {admin && (
                        <button
                            onClick={shadowAdmin}
                            className="w-7 h-7 grid place-items-center rounded-lg text-text_3 hover:bg-layer_3 hover:text-text_main transition-colors"
                            title="Admin mode"
                        >
                            <Image src="/icons/gear.svg" alt="" width={14} height={14} />
                        </button>
                    )}
                </div>
            </div>

            <div
                ref={listRef}
                className="flex-1 overflow-y-auto overscroll-contain fluent-scrollbar px-3 py-2.5 flex flex-col gap-2"
            >
                {messages.map((m) => {
                    const lvl = Math.floor((m.user.experience ?? 0) / 100);
                    const isMod = m.user.status !== 'USER';
                    const nickColor = isMod ? '#46e08a' : getLevelColor(lvl);
                    return (
                        <div
                            key={m.id}
                            className="grid grid-cols-[34px_minmax(0,1fr)] gap-2.5 px-2 py-1.5 rounded-fl hover:bg-layer_2 transition-colors group"
                        >
                            <AdminAvatarMenu userId={m.userId} userName={m.user.name} position="left">
                                <span
                                    className="block w-[34px] h-[34px] rounded-[28%]"
                                    style={{
                                        boxShadow: `0 0 12px -3px ${nickColor}, 0 0 4px -1px ${nickColor}`,
                                    }}
                                >
                                    <AbstractAvatar
                                        seed={m.user.name || String(m.userId)}
                                        src={m.user.avatar}
                                        size={34}
                                        square
                                        className="!w-full !h-full"
                                    />
                                </span>
                            </AdminAvatarMenu>
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <LevelBadge level={lvl} size="sm" variant="solid" color={getLevelColor(lvl)} />
                                    <span
                                        className="font-chat-nick text-[13px] font-bold truncate"
                                        style={{
                                            color: nickColor,
                                            textShadow: `0 0 8px color-mix(in srgb, ${nickColor} 55%, transparent)`,
                                        }}
                                    >
                                        {m.user.name}
                                    </span>
                                    {admin && (
                                        <span className="ml-auto opacity-0 group-hover:opacity-100 flex gap-1.5">
                                            <button onClick={() => deleteMessage(m.id)} aria-label="Delete">
                                                <Image
                                                    src={'/icons/deleteMessage.svg'}
                                                    alt=""
                                                    width={12}
                                                    height={12}
                                                />
                                            </button>
                                            <button
                                                onClick={() => banUser(m.userId, m.user.name)}
                                                aria-label="Ban"
                                            >
                                                <Image src={'/icons/ban.svg'} alt="" width={12} height={12} />
                                            </button>
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="font-chat-msg text-[13px] text-text_2 leading-[1.45] break-words"
                                    dangerouslySetInnerHTML={{
                                        __html: replaceEmojiCodesInText(m.message),
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="px-3 py-2.5 border-t border-stroke">
                <div className="flex items-center bg-layer_2 border border-stroke rounded-fl pl-3 pr-1 py-1 gap-1.5 focus-within:border-accent focus-within:bg-layer_3 transition-colors">
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') send();
                        }}
                        disabled={!isAuthed}
                        placeholder={isAuthed ? 'Type a message…' : 'Sign in to chat'}
                        className="flex-1 min-w-0 bg-transparent outline-none text-text_main placeholder:text-text_4 text-[12.5px] py-2"
                    />
                    {isAuthed ? (
                        <>
                            <div className="relative">
                                <EmojiPick
                                    state={picker}
                                    insert={(code: string) => {
                                        setDraft((d) => d + `:${code}:`);
                                        setPicker(false);
                                    }}
                                    setVisible={setPicker}
                                />
                            </div>
                            <button
                                onClick={send}
                                aria-label="Send"
                                className="w-8 h-8 grid place-items-center rounded-lg bg-accent text-[#0a151c] hover:brightness-110 transition"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                                >
                                    <path d="M3 12l18-9-4 18-5-7-9-2z" />
                                </svg>
                            </button>
                        </>
                    ) : (
                        <Login noIcon />
                    )}
                </div>
            </div>
        </aside>
    );
};

export default Chat;
